import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Switch,
  Checkbox,
  Tooltip,
  Divider,
  Alert,
} from '@mui/material';
import {
  Calculate as MathIcon,
  Science as ScienceIcon,
  AccountBalance as HistoryIcon,
  Translate as LanguagesIcon,
  Edit as EnglishIcon,
  Palette as ArtsIcon,
  Computer as ComputingIcon,
  AutoAwesome as OtherIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Shield as ShieldIcon,
  Description as DescriptionIcon,
  MenuBook as SchoolTemplateIcon,
  InsertDriveFile as StartFreshIcon,
  Explore as HintIcon,
  Timer as PacingIcon,
  SentimentSatisfiedAlt as ToneIcon,
  RateReview as FeedbackIcon,
  Visibility as PreviewIcon,
  Psychology as MisconceptionIcon,
  AutoStories as MaterialsIcon,
  Summarize as GlanceIcon,
  School as ClassesIcon,
  RocketLaunch as PublishIcon,
} from '@mui/icons-material';
import { tutorTokens } from '../../../theme/tutorTheme';
import config from '../../../config';
import {
  HINT_STYLES,
  TONE_OPTIONS,
  FEEDBACK_STYLES,
  PACING_OPTIONS,
  GRADE_BANDS,
  SUBJECTS,
  defaultAgentDraft,
  saveAgent,
} from '../../../utils/agentTutorStorage';
import TutorWorkspace from '../../agentTutor/TutorWorkspace';
import SelectableTile from './SelectableTile';
import OptionCard from './OptionCard';
import { SectionTitle, IconBadge } from './PanelTitles';
import TutoringRulesField from './TutoringRulesField';
import normalizeAgent, { nextLocalId } from './normalizeAgent';

const SUBJECT_ICONS = {
  math: <MathIcon />,
  science: <ScienceIcon />,
  history: <HistoryIcon />,
  languages: <LanguagesIcon />,
  english: <EnglishIcon />,
  arts: <ArtsIcon />,
  computing: <ComputingIcon />,
  other: <OtherIcon />,
};

// Human labels for the activities map's camelCase keys — the summary card
// used to print the raw key ("Activities: stepByStep") because
// Object.entries(...).map(([k]) => k) never translated it. See ACTIVITY_LIST
// in step 3 for the same labels used at toggle-time.
//
// `practiceTests` is deliberately absent: the backend's
// `_available_response_types` (app/routes/agent_tutor.py) has no mapping for
// it, so there is no student-visible effect to label. The field itself still
// lives in `defaultAgentDraft` and the backend's `Activities` model so an
// older agent that has it set doesn't break on load — see the step-3 filter
// below, which drops it from the glance summary even if it's `true`.
const ACTIVITY_LABELS = {
  stepByStep: 'Step-by-step',
  flashcards: 'Flashcards',
  quizzes: 'Quizzes',
};

const STEPS = ['The Basics', 'Teaching Style', 'Activities & Materials', 'Try It & Publish'];

const focusRingSx = {
  '&:focus-visible': { outline: `2px solid ${tutorTokens.accent}`, outlineOffset: 2 },
};

const panelSx = {
  p: tutorTokens.layout.cardPad,
  borderRadius: `${tutorTokens.radiusLg}px`,
  backgroundColor: tutorTokens.surfaceRaised,
  border: `1px solid ${tutorTokens.border}`,
  boxShadow: tutorTokens.shadowCard,
};

// One geometry, three states — this used to be three different visual
// treatments (a green pill with the number dropped when complete, a
// filled indigo pill with the number kept when active, bare text with no
// container at all when pending), which meant every item's rendered width
// changed as its own state changed, and the whole row visibly shifted
// horizontally as the user advanced through the wizard. Now every item is
// the same pill shape and padding in all three states — a fixed-size
// circular badge (number or ✓, swapped but never resized) plus a label
// whose text is identical in every state (no number embedded in it, so
// nothing to add or drop) — differentiated only by border/fill/color.
// Since neither the badge's size nor the label's text ever changes with
// `step`, each item's own rendered width is now constant regardless of
// which step is active, and the connectors between them (a fixed-width
// line, not one that stretches to fill whatever gap variable-width pills
// used to leave) are identical in every position.
function StepHeader({ step }) {
  return (
    <Box
      role="list"
      aria-label="Setup progress"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4, flexWrap: 'wrap' }}
    >
      {STEPS.map((label, idx) => {
        const num = idx + 1;
        const done = num < step;
        const active = num === step;
        const stateColor = done ? tutorTokens.correct : active ? tutorTokens.accent : tutorTokens.inkSoft;
        return (
          <Box key={label} role="listitem" aria-current={active ? 'step' : undefined} sx={{ display: 'flex', alignItems: 'center' }}>
            {idx > 0 && (
              <Box
                aria-hidden="true"
                sx={{ width: 32, height: 2, flexShrink: 0, backgroundColor: done ? tutorTokens.correct : tutorTokens.border }}
              />
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.625,
                borderRadius: 999,
                border: `1.5px solid ${done ? tutorTokens.correctBorder : active ? tutorTokens.accent : tutorTokens.border}`,
                backgroundColor: done ? tutorTokens.correctBg : active ? tutorTokens.accentLight : 'transparent',
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...tutorTokens.type.eyebrow,
                  fontVariantNumeric: 'tabular-nums',
                  backgroundColor: done || active ? stateColor : 'transparent',
                  border: done || active ? 'none' : `1.5px solid ${tutorTokens.borderStrong}`,
                  color: done || active ? '#fff' : tutorTokens.inkSoft,
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 14 }} /> : num}
              </Box>
              <Typography sx={{ ...tutorTokens.type.label, fontWeight: 700, whiteSpace: 'nowrap', color: stateColor }}>
                {label}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

const AgentBuilderWizard = ({
  currentUser,
  groups = [],
  existingAgent,
  showcaseAgent,
  // Whether the last attempt to load `showcaseAgent` failed outright (a
  // thrown fetch), as opposed to succeeding with "no showcase agent in this
  // deployment." The two look identical from `showcaseAgent` alone
  // (both `null`-ish), but they're different situations for a teacher: one
  // is "ask again later," the other is "this school genuinely has no
  // template."
  showcaseFetchFailed = false,
  onCancel,
  onPublished,
  onSaveDraft,
}) => {
  // `startMode` is a creation-time choice — "where would you like to start?"
  // has no meaning once an agent already exists. Editing an existing agent
  // (via the library or MathTutorConfig's "Edit in builder", which can open
  // straight on the showcase agent) never renders the start-mode selector at
  // all, which is what keeps `applyTemplate`/`applyFresh` — both destructive
  // resets — structurally unreachable from an edit flow, not just guarded by
  // a condition someone could forget to check.
  const isEditingExisting = !!existingAgent;
  const [step, setStep] = useState(1);
  const [agent, setAgent] = useState(() => normalizeAgent(existingAgent || defaultAgentDraft(currentUser)));
  const [materials, setMaterials] = useState([]);
  const [misconceptionDraft, setMisconceptionDraft] = useState({ mistake: '', response: '' });

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/api/curriculum/documents`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { documents: [] }))
      .then((data) => setMaterials(data.documents || []))
      .catch(() => setMaterials([]));
  }, []);

  const update = (patch) => setAgent((a) => ({ ...a, ...patch }));

  const toggleGrade = (g) =>
    update({ grades: agent.grades.includes(g) ? agent.grades.filter((x) => x !== g) : [...agent.grades, g] });

  const toggleGroup = (id) =>
    update({
      assignedGroupIds: agent.assignedGroupIds.includes(id)
        ? agent.assignedGroupIds.filter((x) => x !== id)
        : [...agent.assignedGroupIds, id],
    });

  const toggleMaterial = (id) =>
    update({ materialIds: agent.materialIds.includes(id) ? agent.materialIds.filter((x) => x !== id) : [...agent.materialIds, id] });

  const addMisconception = () => {
    if (!misconceptionDraft.mistake.trim()) return;
    update({ misconceptions: [...agent.misconceptions, { id: nextLocalId(), ...misconceptionDraft }] });
    setMisconceptionDraft({ mistake: '', response: '' });
  };

  const removeMisconception = (id) =>
    update({ misconceptions: agent.misconceptions.filter((m) => m.id !== id) });

  // Everything "School template" is allowed to pre-fill and "Start fresh" is
  // allowed to clear — teaching craft only, never identity/ownership. This
  // list is the one place both directions of that boundary are enforced, so
  // adding a field to one side without the other isn't possible by accident.
  const templateAvailable = !!showcaseAgent;
  const templateUnavailableReason = showcaseFetchFailed
    ? "Couldn't check for a school template just now — try again in a moment."
    : 'No school template is set up for your school.';

  // Both handlers below are a full reset of the same 10 fields, in opposite
  // directions. Guarding on "already in that mode" is what makes re-clicking
  // the already-selected tile a no-op instead of silently re-running the
  // reset — before this guard, a click that changed nothing about the
  // *selection* still wiped anything the teacher had since typed into
  // grades/welcome message/misconceptions etc.
  const applyTemplate = () => {
    if (!showcaseAgent || agent.startMode === 'template') return;
    const defaults = defaultAgentDraft(currentUser);
    update({
      startMode: 'template',
      hintStyle: showcaseAgent.hintStyle || defaults.hintStyle,
      tone: showcaseAgent.tone || defaults.tone,
      feedbackStyle: showcaseAgent.feedbackStyle || defaults.feedbackStyle,
      pacing: showcaseAgent.pacing || defaults.pacing,
      grades: showcaseAgent.grades?.length ? showcaseAgent.grades : defaults.grades,
      activities: { ...defaults.activities, ...(showcaseAgent.activities || {}) },
      // The showcase agent's misconceptions come back as bare
      // {mistake, response} — no id, same as any server response (see
      // normalizeAgent.js) — so they need a local id before they can be
      // rendered/removed like any other misconception in this list.
      misconceptions: (showcaseAgent.misconceptions || []).map((m) => ({
        id: nextLocalId(),
        mistake: m.mistake,
        response: m.response || '',
      })),
      welcomeMessage: showcaseAgent.welcomeMessage || '',
      tutoringRules: showcaseAgent.tutoringRules || '',
    });
  };

  const applyFresh = () => {
    if (agent.startMode === 'fresh') return;
    const defaults = defaultAgentDraft(currentUser);
    update({
      startMode: 'fresh',
      hintStyle: defaults.hintStyle,
      tone: defaults.tone,
      feedbackStyle: defaults.feedbackStyle,
      pacing: defaults.pacing,
      grades: defaults.grades,
      activities: defaults.activities,
      misconceptions: defaults.misconceptions,
      welcomeMessage: defaults.welcomeMessage,
      tutoringRules: defaults.tutoringRules,
    });
  };

  const previewLine = useMemo(() => {
    const hint = HINT_STYLES.find((h) => h.value === agent.hintStyle);
    return hint ? hint.example : '';
  }, [agent.hintStyle]);

  const canAdvance = step === 1 ? agent.name.trim().length > 0 && agent.grades.length > 0 : true;

  const goNext = () => setStep((s) => Math.min(4, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const persist = async (publish, onDone) => {
    setSaving(true);
    setSaveError('');
    try {
      const saved = normalizeAgent(await saveAgent(agent, { publish, currentUser }));
      setAgent(saved);
      onDone && onDone(saved);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => persist(false, onSaveDraft);
  const handlePublish = () => persist(true, onPublished);

  return (
    // No `minHeight: '100vh'` — this only ever mounts inside
    // AdminDashboardV2's `component="main"`, which already reserves it.
    //
    // Breadcrumb, step indicator, and every step's own content now share
    // one `maxWidth` frame (`tutorTokens.layout.maxWidth`, the same 1240
    // every other Agent Tutor page uses) instead of each floating at its
    // own width inside an unbounded canvas — that's what used to leave the
    // breadcrumb pinned to the far-left edge with nothing beneath it, and
    // the step indicator centered in space with no visual relationship to
    // the (much narrower) card below it. Everything below now belongs to
    // one column, and any wide-viewport slack is a deliberate, even
    // margin either side of that column, not empty space this page just
    // stops paying attention to past ~900px.
    <Box sx={{ p: tutorTokens.layout.gutter, backgroundColor: tutorTokens.surface }}>
      <Box sx={{ maxWidth: tutorTokens.layout.maxWidth, mx: 'auto' }}>
        <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft, mb: 1 }}>
          Agent Tutor › Build Your Own Agent
        </Typography>

        <StepHeader step={step} />

        {step === 1 && (
          // Reuses steps 2-4's own `1.4fr 1fr` grid, with nothing placed
          // in the second column, rather than computing a matching width
          // as a separate constant. A duplicated `calc()` meant to mirror
          // this ratio was tried first and got the *math* right but the
          // *basis* wrong — expressed in `vw` (viewport width), so inside
          // the admin shell's 280px sidebar it resolved against the full
          // browser window instead of the real ~1322px content area,
          // landing at 910px instead of the 757px steps 2-4 actually
          // render at. Reusing the grid itself can't drift that way: the
          // left column is *structurally* the same width as steps 2-4's,
          // computed by the same grid against the same real container,
          // not a second formula that has to be kept in sync by hand. It
          // also left-aligns this card to the frame edge for free — grid
          // items don't center themselves — matching steps 2-4's own
          // left-aligned columns instead of floating centered next to
          // them.
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 3 }}>
          <Box sx={{ ...panelSx, p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'center', color: tutorTokens.ink, mb: 0.5 }}>
            Let's build your teaching agent
          </Typography>
          <Typography sx={{ textAlign: 'center', color: tutorTokens.inkSoft, mb: 4 }}>
            Answer a few questions about how you teach — no technical skills needed. You can change anything later.
          </Typography>

          {/* `startMode` is a creation-time-only choice — see isEditingExisting above.
              Hiding this whole section when editing an existing agent (including the
              showcase agent, reachable via MathTutorConfig's "Edit in builder") is what
              makes applyTemplate/applyFresh structurally unreachable from an edit flow. */}
          {!isEditingExisting && (
            <>
              <Typography sx={{ fontWeight: 700, mb: 1, color: tutorTokens.ink }}>Where would you like to start?</Typography>
              <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, mb: 1.5 }}>
                Starting from a template is easiest — everything below stays editable.
              </Typography>
              <Box role="radiogroup" aria-label="Starting point" sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Tooltip title={templateAvailable ? '' : templateUnavailableReason}>
                  <span style={{ flex: 1, minWidth: 220 }}>
                    <SelectableTile
                      selected={agent.startMode === 'template'}
                      disabled={!templateAvailable}
                      onSelect={applyTemplate}
                      ariaLabel={
                        templateAvailable
                          ? "School template — Math Tutor, your school's default setup"
                          : `School template — ${templateUnavailableReason}`
                      }
                      sx={{ flex: 1, minWidth: 220 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: tutorTokens.ink }}>
                        <SchoolTemplateIcon fontSize="small" /> School template
                      </Box>
                      <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, mt: 0.5 }}>
                        {templateAvailable ? "Math Tutor — your school's default setup" : 'Currently unavailable'}
                      </Typography>
                    </SelectableTile>
                  </span>
                </Tooltip>
                {/* "From the Marketplace" used to render here as a
                    permanently-disabled tile — Marketplace is out of this
                    build's scope per the SOW, and a dead control next to
                    two live ones reads as broken. Removed for the same
                    reason and the same way as the library page's dead
                    "Browse Marketplace" button: nothing for it to do yet,
                    so it isn't shown as a choice at all rather than shown
                    and refused. */}
                <SelectableTile
                  selected={agent.startMode === 'fresh'}
                  onSelect={applyFresh}
                  ariaLabel="Start fresh — a blank agent with safe defaults"
                  sx={{ flex: 1, minWidth: 220 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: tutorTokens.ink }}>
                    <StartFreshIcon fontSize="small" /> Start fresh
                  </Box>
                  <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, mt: 0.5 }}>A blank agent with safe defaults</Typography>
                </SelectableTile>
              </Box>

              {/* `role="status"` (polite) rather than Alert's assertive default — this
                  confirms the outcome of a click the teacher just made themselves, which
                  aria-checked on the tile already announced; it isn't an unprompted error. */}
              {agent.startMode === 'template' && templateAvailable && (
                <Alert
                  icon={<SchoolTemplateIcon fontSize="small" />}
                  severity="info"
                  role="status"
                  sx={{
                    mb: 3,
                    borderRadius: `${tutorTokens.radiusSm}px`,
                    backgroundColor: tutorTokens.accentLight,
                    color: tutorTokens.ink,
                    border: `1px solid ${tutorTokens.accentBorder}`,
                    '& .MuiAlert-icon': { color: tutorTokens.accent },
                  }}
                >
                  Your agent now teaches the way Math Tutor does, including the grades and welcome message below —
                  review this step and the next two, and change anything that doesn't fit.
                </Alert>
              )}
              {agent.startMode === 'template' && !templateAvailable && (
                <Alert
                  severity="info"
                  role="status"
                  sx={{
                    mb: 3,
                    borderRadius: `${tutorTokens.radiusSm}px`,
                    backgroundColor: tutorTokens.partialBg,
                    color: tutorTokens.ink,
                    border: `1px solid ${tutorTokens.partialBorder}`,
                    '& .MuiAlert-icon': { color: tutorTokens.partial },
                  }}
                >
                  {templateUnavailableReason} Starting as a blank agent instead.
                </Alert>
              )}

              <Divider sx={{ my: 3, borderColor: tutorTokens.border }} />
            </>
          )}

          <Typography component="label" htmlFor="agent-name-input" sx={{ display: 'block', fontWeight: 700, mb: 1, color: tutorTokens.ink }}>
            What should students call your agent?
          </Typography>
          <TextField
            id="agent-name-input"
            fullWidth
            placeholder="Ms. Rivera's Algebra Helper"
            value={agent.name}
            onChange={(e) => update({ name: e.target.value })}
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: `${tutorTokens.radiusMd}px` } }}
          />

          <Typography component="label" htmlFor="agent-welcome-input" sx={{ display: 'block', fontWeight: 700, mb: 0.5, color: tutorTokens.ink }}>
            What's the first message students see?{' '}
            <Typography component="span" sx={{ ...tutorTokens.type.body, fontWeight: 500, color: tutorTokens.inkSoft }}>(optional)</Typography>
          </Typography>
          <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, mb: 1.5 }}>
            The greeting your agent opens with. Leave it blank and students get a friendly default.
          </Typography>
          <TextField
            id="agent-welcome-input"
            fullWidth
            multiline
            minRows={2}
            placeholder={`"Hi! I'm here to help you practice fraction word problems — want to start with today's homework, or pick your own problem?"`}
            value={agent.welcomeMessage || ''}
            onChange={(e) => update({ welcomeMessage: e.target.value.slice(0, 240) })}
            inputProps={{ maxLength: 240, 'aria-describedby': 'agent-welcome-count' }}
            sx={{ mb: 0.5, '& .MuiOutlinedInput-root': { borderRadius: `${tutorTokens.radiusMd}px` } }}
          />
          <Typography id="agent-welcome-count" variant="caption" sx={{ display: 'block', textAlign: 'right', color: tutorTokens.inkFaint, mb: 3 }}>
            {(agent.welcomeMessage || '').length}/240
          </Typography>

          <Divider sx={{ my: 3, borderColor: tutorTokens.border }} />

          <Typography sx={{ fontWeight: 700, mb: 1, color: tutorTokens.ink }}>Which subject?</Typography>
          <Box role="radiogroup" aria-label="Subject" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
            {SUBJECTS.map((s) => (
              <Tooltip key={s.value} title={s.enabled ? '' : 'Coming soon — Math is the only fully built subject right now'}>
                <span>
                  <SelectableTile
                    selected={agent.subject === s.value}
                    disabled={!s.enabled}
                    onSelect={() => update({ subject: s.value })}
                    ariaLabel={s.enabled ? s.label : `${s.label} — coming soon`}
                    sx={{ width: 92, textAlign: 'center' }}
                  >
                    <Box sx={{ color: agent.subject === s.value ? tutorTokens.accent : tutorTokens.inkSoft, display: 'flex', justifyContent: 'center' }}>
                      {SUBJECT_ICONS[s.value]}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: tutorTokens.ink }}>{s.label}</Typography>
                  </SelectableTile>
                </span>
              </Tooltip>
            ))}
          </Box>

          <Typography sx={{ fontWeight: 700, mb: 1, color: tutorTokens.ink }}>Which grades?</Typography>
          <Box role="group" aria-label="Grade bands" sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {GRADE_BANDS.map((g) => {
              const selected = agent.grades.includes(g);
              return (
                <Chip
                  key={g}
                  label={g}
                  onClick={() => toggleGrade(g)}
                  aria-pressed={selected}
                  sx={{
                    fontWeight: 700,
                    height: 40,
                    px: 0.5,
                    backgroundColor: selected ? tutorTokens.accent : tutorTokens.surface,
                    color: selected ? '#fff' : tutorTokens.ink,
                    border: `1px solid ${selected ? tutorTokens.accent : tutorTokens.border}`,
                    ...focusRingSx,
                  }}
                />
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={onCancel} sx={{ color: tutorTokens.inkSoft, fontWeight: 600, minHeight: 44, ...focusRingSx }}>Cancel</Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={!canAdvance}
              onClick={goNext}
              sx={{
                backgroundColor: tutorTokens.accent,
                fontWeight: 700,
                minHeight: 44,
                transition: `background-color ${tutorTokens.durFast}s ease, box-shadow ${tutorTokens.durFast}s ease`,
                '&:hover': { backgroundColor: tutorTokens.accentDark, boxShadow: tutorTokens.shadowHover },
                ...focusRingSx,
              }}
            >
              Next: Teaching Style
            </Button>
          </Box>
          </Box>
        </Box>
      )}

      {step === 2 && (
        // No per-step `maxWidth` wrapper any more — the shared frame above
        // already sets it, the same as steps 3 and 4.
        <>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, textAlign: 'center', color: tutorTokens.ink, mb: 0.5 }}
          >
            How do you teach?
          </Typography>
          <Typography sx={{ textAlign: 'center', color: tutorTokens.inkSoft, mb: 4 }}>
            These choices are your teaching craft, captured — every option shows exactly what your agent would say to a student.
          </Typography>

          {/* `1.4fr 1fr`, matching steps 3 and 4's own two-column split —
              this panel carries four dense option groups (Hint, Pacing,
              Tone, Feedback) against a short Live Preview + Misconceptions
              column, the same "more content, more room" shape those later
              steps already give their own left column. Step 2 alone used
              to split this `1fr 1fr`, which is most of why its option text
              had the least room on the page. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 3 }}>
            <Box sx={panelSx}>
              {/* Hint and Pacing stack in one column always now — no
                  `twoColAt760`. That helper split into two columns once the
                  *viewport* passed 760px, but this panel is only ever half
                  of a `1.4fr 1fr` row inside a ≤1240px frame: at any
                  desktop width the panel itself is ~650-700px, so the old
                  media query (which can't see the panel's own rendered
                  width, only the viewport's) split it into two ~300px
                  sub-columns regardless — precisely the "worst wrapping on
                  the site" case (a 4-6 word option label breaking across
                  2-3 lines). One column at the panel's full width gives
                  every option label the room a `minWidth` alone couldn't:
                  see OptionCard/SelectableTile for that floor. */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <SectionTitle icon={HintIcon} sx={{ mb: 1.5 }}>When a student is stuck, your agent should…</SectionTitle>
                  <Box role="radiogroup" aria-label="Hinting strategy">
                    {HINT_STYLES.map((h) => (
                      <OptionCard
                        key={h.value}
                        selected={agent.hintStyle === h.value}
                        label={h.label}
                        consequence={h.example}
                        onSelect={() => update({ hintStyle: h.value })}
                      />
                    ))}
                  </Box>
                </Box>

                <Box>
                  <SectionTitle icon={PacingIcon} sx={{ mb: 1.5 }}>When should it move to the next topic?</SectionTitle>
                  <Box role="radiogroup" aria-label="Pacing and mastery">
                    {PACING_OPTIONS.map((p) => (
                      <OptionCard
                        key={p.value}
                        selected={agent.pacing === p.value}
                        label={p.label}
                        consequence={p.hint}
                        onSelect={() => update({ pacing: p.value })}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: tutorTokens.border }} />

              <SectionTitle icon={ToneIcon} sx={{ mb: 1.5 }}>How encouraging should it sound?</SectionTitle>
              <Box role="radiogroup" aria-label="Tone" sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {TONE_OPTIONS.map((t) => {
                  const selected = agent.tone === t.value;
                  return (
                    <Chip
                      key={t.value}
                      label={t.label}
                      onClick={() => update({ tone: t.value })}
                      role="radio"
                      aria-checked={selected}
                      sx={{
                        fontWeight: 700,
                        height: 44,
                        px: 0.5,
                        backgroundColor: selected ? tutorTokens.accent : tutorTokens.surface,
                        color: selected ? '#fff' : tutorTokens.ink,
                        border: `1px solid ${selected ? tutorTokens.accent : tutorTokens.border}`,
                        ...focusRingSx,
                      }}
                    />
                  );
                })}
              </Box>

              <Divider sx={{ my: 2, borderColor: tutorTokens.border }} />

              <SectionTitle icon={FeedbackIcon} sx={{ mb: 0.5 }}>How should it respond to mistakes?</SectionTitle>
              <Typography variant="caption" sx={{ color: tutorTokens.inkSoft, display: 'block', mb: 1 }}>
                This only shapes the wording of feedback — the agent doesn't score or grade work.
              </Typography>
              <Box role="radiogroup" aria-label="Feedback style">
                {FEEDBACK_STYLES.map((f) => (
                  <OptionCard
                    key={f.value}
                    selected={agent.feedbackStyle === f.value}
                    label={f.label}
                    consequence={f.example}
                    onSelect={() => update({ feedbackStyle: f.value })}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Box sx={{ ...panelSx, mb: 3 }}>
                <SectionTitle icon={PreviewIcon} sx={{ mb: 1.5 }}>Live preview</SectionTitle>
                <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, mb: 2 }}>
                  Solve for x: −2x + 6 = 12
                </Typography>
                <Box sx={{ p: 2, borderRadius: `${tutorTokens.radiusMd}px`, backgroundColor: tutorTokens.hintBg, border: `1px solid ${tutorTokens.hintBorder}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: tutorTokens.hint }}>Your hint appears on the broken step:</Typography>
                  {/* No added quote marks — `HINT_STYLES[...].example` already
                      supplies its own where the text is a quoted line of
                      speech ("socratic"/"nudge"); "worked_example"'s entry is
                      a plain description with none. Wrapping every value in
                      `"…"` here doubled the quotes on two of the three
                      options instead of respecting what the data already
                      decided. */}
                  <Typography variant="body2" sx={{ color: tutorTokens.ink }}>{previewLine}</Typography>
                </Box>
              </Box>

              <Box sx={panelSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
                  <IconBadge icon={MisconceptionIcon} size={32} />
                  <Typography component="h5" sx={{ ...tutorTokens.type.subtitle, color: tutorTokens.ink, m: 0 }}>
                    What do your students get wrong every year?
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, mb: 2 }}>
                  Your misconception library — the agent watches for these exact mistakes and responds the way you would.
                </Typography>
                {agent.misconceptions.map((m) => (
                  <Box
                    key={m.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.5,
                      mb: 1,
                      borderRadius: `${tutorTokens.radiusMd}px`,
                      backgroundColor: tutorTokens.surface,
                      border: `1px solid ${tutorTokens.border}`,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: tutorTokens.ink }}>{m.mistake}</Typography>
                      {m.response && <Typography variant="body2" sx={{ color: tutorTokens.inkSoft }}>→ {m.response}</Typography>}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeMisconception(m.id)}
                      aria-label={`Remove misconception: ${m.mistake}`}
                      sx={{ width: 44, height: 44, color: tutorTokens.inkSoft, '&:hover': { color: tutorTokens.incorrect }, ...focusRingSx }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. Drops the negative when dividing"
                  value={misconceptionDraft.mistake}
                  onChange={(e) => setMisconceptionDraft((d) => ({ ...d, mistake: e.target.value }))}
                  inputProps={{ 'aria-label': 'Misconception — what students get wrong' }}
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: `${tutorTokens.radiusSm}px` } }}
                />
                <TextField
                  size="small"
                  fullWidth
                  placeholder="How should the agent respond? (optional)"
                  value={misconceptionDraft.response}
                  onChange={(e) => setMisconceptionDraft((d) => ({ ...d, response: e.target.value }))}
                  inputProps={{ 'aria-label': "Agent's response (optional)" }}
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: `${tutorTokens.radiusSm}px` } }}
                />
                <Button
                  startIcon={<AddIcon />}
                  onClick={addMisconception}
                  sx={{ color: tutorTokens.accent, fontWeight: 700, minHeight: 44, ...focusRingSx }}
                >
                  Add a misconception
                </Button>
              </Box>

              <Box sx={{ ...panelSx, mt: 3 }}>
                <TutoringRulesField
                  value={agent.tutoringRules}
                  onChange={(v) => update({ tutoringRules: v })}
                  idPrefix="wizard-tutoring-rules"
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ color: tutorTokens.inkSoft, fontWeight: 600, minHeight: 44, ...focusRingSx }}>Back</Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={goNext}
              sx={{
                backgroundColor: tutorTokens.accent,
                fontWeight: 700,
                minHeight: 44,
                transition: `background-color ${tutorTokens.durFast}s ease, box-shadow ${tutorTokens.durFast}s ease`,
                '&:hover': { backgroundColor: tutorTokens.accentDark, boxShadow: tutorTokens.shadowHover },
                ...focusRingSx,
              }}
            >
              Next: Activities & Materials
            </Button>
          </Box>
        </>
      )}

      {step === 3 && (
        <>
          <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'center', color: tutorTokens.ink, mb: 0.5 }}>
            What should your agent do with students?
          </Typography>
          <Typography sx={{ textAlign: 'center', color: tutorTokens.inkSoft, mb: 4 }}>
            Turn on the activities you want, and attach your own materials — so it's tied to your class, not a generic one.
          </Typography>

          {/* `repeat(auto-fit, minmax(220px, 340px))`, capped rather than
              `1fr`-stretched — with only three activity toggles, letting
              each track grow all the way to `1fr` on a wide/fluid frame
              stretched each tile to 600px+ of near-empty space around a
              switch and two lines of text, which is its own kind of
              sparse. Capping the track and centering the row with
              `justifyContent: 'center'` keeps the three tiles a
              comfortable, consistent width and groups them as one block
              instead of spreading them thin across the full frame. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(220px, 340px))' },
              justifyContent: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            {[
              { key: 'stepByStep', label: 'Step-by-step problems', desc: "Agent checks each step of a student's working", locked: true },
              { key: 'flashcards', label: 'Flashcards', desc: 'Quick recall checks between problems' },
              { key: 'quizzes', label: 'Quizzes', desc: 'Multiple-choice, one question at a time' },
            ].map((a) => {
              const on = !!agent.activities[a.key];
              return (
                // `role="switch"` (see SelectableTile) — each of these is an
                // independent on/off toggle, not a mutually-exclusive choice, so
                // the tile itself is the accessible control. The nested MUI
                // `Switch` below is a visual echo only (aria-hidden, unfocusable,
                // pointer-events off) so it isn't announced as a second control.
                <SelectableTile
                  key={a.key}
                  role="switch"
                  selected={on}
                  disabled={a.locked}
                  onSelect={() => update({ activities: { ...agent.activities, [a.key]: !on } })}
                  ariaLabel={`${a.label} — ${a.locked ? 'always on' : on ? 'on' : 'off'}`}
                  sx={a.locked ? { p: 2, opacity: 1, cursor: 'default' } : { p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 700, color: tutorTokens.ink }}>{ACTIVITY_LABELS[a.key] || a.label}</Typography>
                    <Switch
                      checked={on}
                      disabled={a.locked}
                      tabIndex={-1}
                      inputProps={{ 'aria-hidden': true, tabIndex: -1 }}
                      sx={{ pointerEvents: 'none' }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: tutorTokens.inkSoft }}>{a.desc}</Typography>
                </SelectableTile>
              );
            })}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 3 }}>
            <Box sx={panelSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
                <IconBadge icon={MaterialsIcon} size={32} />
                <Typography component="h5" sx={{ ...tutorTokens.type.subtitle, color: tutorTokens.ink, m: 0 }}>Attach your materials</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, mb: 2 }}>
                Pick documents from your Curriculum library — they'll be attached to this agent.
              </Typography>
              {materials.length === 0 && (
                <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, fontStyle: 'italic' }}>
                  No curriculum documents uploaded yet — upload some from the Curriculum tab to connect them here.
                </Typography>
              )}
              {materials.map((doc) => (
                <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, minHeight: 44 }}>
                  <Checkbox
                    size="small"
                    checked={agent.materialIds.includes(doc.id)}
                    onChange={() => toggleMaterial(doc.id)}
                    icon={<DescriptionIcon fontSize="small" />}
                    checkedIcon={<CheckIcon fontSize="small" sx={{ color: tutorTokens.correct }} />}
                    inputProps={{ 'aria-label': doc.filename }}
                    sx={focusRingSx}
                  />
                  <Typography variant="body2" sx={{ flex: 1, color: tutorTokens.ink }}>{doc.filename}</Typography>
                  <Typography variant="caption" sx={{ color: tutorTokens.inkSoft }}>{doc.class_name || '—'}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ ...panelSx, backgroundColor: tutorTokens.accentLight, border: `1px solid ${tutorTokens.accentBorder}`, boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ShieldIcon sx={{ color: tutorTokens.accent }} aria-hidden="true" />
                <Typography component="h5" sx={{ fontWeight: 700, color: tutorTokens.ink, m: 0 }}>Safety is built in</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, mb: 1 }}>Every agent automatically follows your school's safety rules:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: tutorTokens.inkSoft }}>
                <li><Typography variant="body2">Flagged messages go to Safety Monitoring for review</Typography></li>
                <li><Typography variant="body2">Age-appropriate language for the grades you selected</Typography></li>
                <li><Typography variant="body2">Never gives the final answer, only guides toward it</Typography></li>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ color: tutorTokens.inkSoft, fontWeight: 600, minHeight: 44, ...focusRingSx }}>Back</Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={goNext}
              sx={{
                backgroundColor: tutorTokens.accent,
                fontWeight: 700,
                minHeight: 44,
                transition: `background-color ${tutorTokens.durFast}s ease, box-shadow ${tutorTokens.durFast}s ease`,
                '&:hover': { backgroundColor: tutorTokens.accentDark, boxShadow: tutorTokens.shadowHover },
                ...focusRingSx,
              }}
            >
              Next: Try It & Publish
            </Button>
          </Box>
        </>
      )}

      {step === 4 && (
        <>
          <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'center', color: tutorTokens.ink, mb: 0.5 }}>
            Meet {agent.name || 'your new agent'}
          </Typography>
          <Typography sx={{ textAlign: 'center', color: tutorTokens.inkSoft, mb: 4 }}>
            Work a problem exactly as a student would. When it feels right, publish it to your classes.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 3, alignItems: 'start' }}>
            <TutorWorkspace agent={agent} mode="embedded" />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={panelSx}>
                <SectionTitle icon={GlanceIcon} sx={{ mb: 1.5 }}>Your agent at a glance</SectionTitle>
                {[
                  ['Subject', `Math · ${agent.grades.join(', ') || '—'}`],
                  ['Welcome message', (agent.welcomeMessage || '').trim() || 'Default greeting'],
                  ['Hints', HINT_STYLES.find((h) => h.value === agent.hintStyle)?.label.split(' — ')[0]],
                  ['Pacing', PACING_OPTIONS.find((p) => p.value === agent.pacing)?.label.split(' — ')[0]],
                  ['Tone', TONE_OPTIONS.find((t) => t.value === agent.tone)?.label],
                  ['Feedback', FEEDBACK_STYLES.find((f) => f.value === agent.feedbackStyle)?.label.split(' — ')[0]],
                  ['Tutoring rules', (agent.tutoringRules || '').trim() || 'None set'],
                  [
                    'Activities',
                    Object.entries(agent.activities)
                      // `practiceTests` is excluded even if a legacy agent
                      // has it set — it has no ACTIVITY_LABELS entry and no
                      // student-visible behavior, so falling back to the
                      // raw key here would surface a broken-looking
                      // "practiceTests" in the summary.
                      .filter(([k, v]) => v && k !== 'practiceTests')
                      .map(([k]) => ACTIVITY_LABELS[k] || k)
                      .join(', ') || '—',
                  ],
                  ['Materials', agent.materialIds.length ? `${agent.materialIds.length} connected` : 'None yet'],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: `1px solid ${tutorTokens.divider}`, '&:last-of-type': { borderBottom: 'none' } }}
                  >
                    <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, flexShrink: 0 }}>{label}</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: tutorTokens.ink,
                        textAlign: 'right',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={panelSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                  <IconBadge icon={PublishIcon} size={32} />
                  <Typography component="h5" sx={{ ...tutorTokens.type.subtitle, color: tutorTokens.ink, m: 0 }}>Publish</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <ClassesIcon sx={{ fontSize: 16, color: tutorTokens.inkSoft }} aria-hidden="true" />
                  <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, fontWeight: 600 }}>Assign to classes</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {groups.length === 0 && (
                    <Typography variant="body2" sx={{ color: tutorTokens.inkSoft, fontStyle: 'italic' }}>
                      No classes yet — add one under Classes & Schedules first.
                    </Typography>
                  )}
                  {groups.map((g) => {
                    const selected = agent.assignedGroupIds.includes(g.id);
                    return (
                      <Chip
                        key={g.id}
                        icon={selected ? <CheckIcon sx={{ fontSize: 16 }} /> : undefined}
                        label={g.school ? `${g.school} · ${g.name}` : g.name}
                        onClick={() => toggleGroup(g.id)}
                        aria-pressed={selected}
                        sx={{
                          fontWeight: 600,
                          height: 40,
                          px: 0.5,
                          backgroundColor: selected ? tutorTokens.accentLight : tutorTokens.surface,
                          color: selected ? tutorTokens.accent : tutorTokens.inkSoft,
                          border: `1px solid ${selected ? tutorTokens.accent : tutorTokens.border}`,
                          ...focusRingSx,
                        }}
                      />
                    );
                  })}
                </Box>

                <Tooltip title="Teacher Marketplace isn't available yet">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, opacity: 0.6, minHeight: 44 }}>
                    <Checkbox
                      size="small"
                      disabled
                      checked={agent.shareToMarketplace}
                      inputProps={{ 'aria-label': 'Also share to the Teacher Marketplace — not available yet' }}
                      sx={focusRingSx}
                    />
                    <Typography variant="body2" sx={{ color: tutorTokens.inkSoft }}>Also share to the Teacher Marketplace so other teachers can adopt or adapt it</Typography>
                  </Box>
                </Tooltip>

                {saveError && (
                  <Alert severity="error" sx={{ mb: 1.5, borderRadius: `${tutorTokens.radiusSm}px` }}>
                    {saveError}
                  </Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  disabled={agent.assignedGroupIds.length === 0 || !agent.name.trim() || saving}
                  onClick={handlePublish}
                  sx={{
                    mb: 1,
                    minHeight: 44,
                    backgroundColor: tutorTokens.accent,
                    fontWeight: 700,
                    boxShadow: tutorTokens.shadowCard,
                    transition: `background-color ${tutorTokens.durFast}s ease, box-shadow ${tutorTokens.durFast}s ease`,
                    '&:hover': { backgroundColor: tutorTokens.accentDark, boxShadow: tutorTokens.shadowHover },
                    ...focusRingSx,
                  }}
                >
                  {saving ? 'Saving…' : `Publish to ${agent.assignedGroupIds.length || ''} class${agent.assignedGroupIds.length === 1 ? '' : 'es'}`}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={saving}
                  onClick={handleSaveDraft}
                  sx={{
                    minHeight: 44,
                    borderColor: tutorTokens.borderStrong,
                    color: tutorTokens.ink,
                    fontWeight: 700,
                    '&:hover': { borderColor: tutorTokens.accent, backgroundColor: tutorTokens.accentLight },
                    ...focusRingSx,
                  }}
                >
                  Save as draft
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ color: tutorTokens.inkSoft, fontWeight: 600, minHeight: 44, ...focusRingSx }}>Back</Button>
          </Box>
        </>
      )}

      {step === 1 && !canAdvance && agent.name.length === 0 && (
        // Same grid-reuse as the step 1 card above, for the same reason —
        // lines the notice up under the card's real edge instead of
        // computing a second, separately-maintained width for it.
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 3 }}>
          <Alert severity="info" sx={{ mt: 2, borderRadius: `${tutorTokens.radiusSm}px` }}>
            Name the agent and pick at least one grade band to continue.
          </Alert>
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default AgentBuilderWizard;
